import { useState, useMemo, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMsal } from '@azure/msal-react';
import {
  receivingApi, lookupsApi, usersApi,
  type ReceivingStatusOption, type LookupOption,
} from '@/lib/apiClient';
import { useFocusTrap } from '@/lib/hooks/useFocusTrap';
import { fmtDate, toDateInputValue } from '@/lib/dates';
import { ReceivingStatusBadge } from './statusBadge';
import { requestorPatch, initialRequestorValue, KEEP_EXTERNAL_REQUESTOR } from './requestorPatch';

// ─── Types ────────────────────────────────────────────────────────────────────

interface StatusRef  { id: number; label: string; phase: 'Active' | 'Closed'; sortOrder: number }
interface MethodRef  { id: number; label: string }
interface ProjectRef { id: number; jobNumber: string; title: string }
interface RequestorRef { id: string; displayName: string | null; email: string | null }

export interface ReceivingOrder {
  id: number;
  title: string;
  projectId: number | null;
  jobNameRaw: string | null;
  itemOrdered: string | null;
  description: string | null;
  quantity: string | null;
  vendor: string | null;
  status: StatusRef;
  deliveryMethod: MethodRef | null;
  requestorId: string | null;
  requestorName: string | null;
  orderDate: string | null;
  expectedDeliveryDate: string | null;
  scheduledArrivalDate: string | null;
  carrier: string | null;
  trackingNumber: string | null;
  trackingUrl: string | null;
  notes: string | null;
  project: ProjectRef | null;
  requestor: RequestorRef | null;
  createdAt: string;
  updatedAt: string;
}

// Common carriers offered in the drawer's Carrier dropdown; "Other…" reveals a
// free-text field so freight carriers (e.g. ArcBest) still work.
const CARRIER_PRESETS = ['UPS', 'FedEx', 'Amazon', 'USPS', 'ArcBest'];

// ─── Sorting ────────────────────────────────────────────────────────────────────

type SortKey = 'item' | 'status' | 'vendor' | 'qty' | 'expected' | 'requestor';
type SortDir = 'asc' | 'desc';

const requestorName = (o: ReceivingOrder) => o.requestorName ?? o.requestor?.displayName ?? '';

function compareOrders(a: ReceivingOrder, b: ReceivingOrder, key: SortKey, dir: SortDir): number {
  const f = dir === 'asc' ? 1 : -1;
  if (key === 'status') {
    return f * ((a.status.sortOrder - b.status.sortOrder)
      || (a.expectedDeliveryDate ?? '9999').localeCompare(b.expectedDeliveryDate ?? '9999'));
  }
  const s = (v: string | null | undefined) => v ?? '';
  let av: string, bv: string;
  switch (key) {
    case 'item':      av = s(a.itemOrdered ?? a.title); bv = s(b.itemOrdered ?? b.title); break;
    case 'vendor':    av = s(a.vendor);                 bv = s(b.vendor);                 break;
    case 'qty':       av = s(a.quantity);               bv = s(b.quantity);               break;
    case 'expected':  av = s(a.expectedDeliveryDate);   bv = s(b.expectedDeliveryDate);   break;
    case 'requestor': av = requestorName(a);            bv = requestorName(b);            break;
    default: return 0;
  }
  return f * av.localeCompare(bv);
}

function Th({ label, sortKey, current, dir, onSort, align }: {
  label: string; sortKey: SortKey; current: SortKey; dir: SortDir; onSort: (k: SortKey) => void; align?: 'left' | 'right';
}) {
  const active = current === sortKey;
  return (
    <th onClick={() => onSort(sortKey)} style={{
      padding: '8px 10px', textAlign: align ?? 'left', whiteSpace: 'nowrap',
      fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.4px',
      color: active ? 'var(--ew)' : 'var(--gray)', cursor: 'pointer', userSelect: 'none',
      borderBottom: '2px solid var(--border)', background: 'var(--surface)',
    }}>
      {label} <span style={{ opacity: active ? 1 : 0.3 }}>{active ? (dir === 'asc' ? '↑' : '↓') : '↕'}</span>
    </th>
  );
}

// ─── Inline detail ──────────────────────────────────────────────────────────────

function DF({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: 12, padding: '3px 0', borderBottom: '1px solid #f3f4f6' }}>
      <span style={{ color: '#6b7280', flexShrink: 0, marginRight: 8 }}>{label}</span>
      <span style={{ fontWeight: 500, textAlign: 'right', color: value ? '#1f2937' : '#9ca3af' }}>{value ?? '—'}</span>
    </div>
  );
}

function DetailSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--ew-r-lg)', padding: '12px 14px' }}>
      <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.8px', color: '#6b7280', marginBottom: 8 }}>{title}</div>
      {children}
    </div>
  );
}

function OrderDetail({ o, canEdit, onEdit, onClose }: { o: ReceivingOrder; canEdit: boolean; onEdit: () => void; onClose: () => void }) {
  const btn: React.CSSProperties = { fontSize: 12, borderRadius: 'var(--ew-r-md)', padding: '5px 12px', cursor: 'pointer', fontWeight: 600 };
  return (
    <div style={{ padding: '14px 20px', background: '#f9fafb', borderTop: '2px solid var(--ew)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <ReceivingStatusBadge label={o.status.label} />
          <span style={{ fontSize: 12, color: 'var(--gray)' }}>{groupLabel(o)}</span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {canEdit && <button onClick={onEdit} style={{ ...btn, background: 'var(--ew)', color: '#fff', border: 'none' }}>Edit</button>}
          <button onClick={onClose} style={{ ...btn, background: '#fff', color: 'var(--gray)', border: '1px solid var(--border)' }}>Close</button>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
        <DetailSection title="Order">
          <DF label="Item" value={o.itemOrdered} />
          <DF label="Quantity" value={o.quantity} />
          <DF label="Vendor" value={o.vendor} />
          <DF label="Delivery Method" value={o.deliveryMethod?.label} />
          <DF label="Requestor" value={requestorName(o) || null} />
        </DetailSection>
        <DetailSection title="Dates">
          <DF label="Order Date" value={fmtDate(o.orderDate)} />
          <DF label="Expected Delivery" value={fmtDate(o.expectedDeliveryDate)} />
          <DF label="Scheduled On-Site" value={fmtDate(o.scheduledArrivalDate)} />
        </DetailSection>
        <DetailSection title="Tracking">
          <DF label="Carrier" value={o.carrier} />
          <DF label="Tracking #" value={o.trackingNumber} />
          <DF label="Link" value={o.trackingUrl
            ? <a href={o.trackingUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--ew)', fontWeight: 600 }}>Open ↗</a>
            : null} />
        </DetailSection>
        {o.description && <DetailSection title="Description"><div style={{ fontSize: 12, whiteSpace: 'pre-wrap', color: '#1f2937' }}>{o.description}</div></DetailSection>}
        {o.notes && <DetailSection title="Notes"><div style={{ fontSize: 12, whiteSpace: 'pre-wrap', color: '#1f2937' }}>{o.notes}</div></DetailSection>}
      </div>
    </div>
  );
}

// ─── Grouping (by project / job, like the Equipment tab) ─────────────────────────

interface ProjectGroup { key: string; label: string; sortKey: string; items: ReceivingOrder[] }

function groupKey(o: ReceivingOrder): string {
  if (o.project) return `p${o.project.id}`;
  if (o.jobNameRaw) return `raw:${o.jobNameRaw}`;
  return 'none';
}
function groupLabel(o: ReceivingOrder): string {
  if (o.project) return `#${o.project.jobNumber} — ${o.project.title}`;
  if (o.jobNameRaw) return o.jobNameRaw;
  return 'General / No project';
}

function groupByProject(rows: ReceivingOrder[], sortKey: SortKey, sortDir: SortDir): ProjectGroup[] {
  const map = new Map<string, ProjectGroup>();
  for (const r of rows) {
    const key = groupKey(r);
    if (!map.has(key)) {
      // Job number drives group order (desc); the "General" bucket sorts last.
      const sortKeyStr = r.project ? `0_${r.project.jobNumber}` : (r.jobNameRaw ? `1_${r.jobNameRaw}` : '2');
      map.set(key, { key, label: groupLabel(r), sortKey: sortKeyStr, items: [] });
    }
    map.get(key)!.items.push(r);
  }
  const groups = [...map.values()].sort((a, b) => b.sortKey.localeCompare(a.sortKey));
  for (const g of groups) g.items.sort((a, b) => compareOrders(a, b, sortKey, sortDir));
  return groups;
}

// ─── New / Edit drawer ───────────────────────────────────────────────────────────

interface DrawerProps {
  item: ReceivingOrder | null;
  statuses: ReceivingStatusOption[];
  methods: LookupOption[];
  projects: { id: number; jobNumber: string; title: string }[];
  onClose: () => void;
  onSaved: () => void;
  /**
   * When set (project-workspace use), the order is pre-scoped to this job:
   * the project picker is replaced by a read-only chip and the project can't
   * be changed. The global Receiving page leaves this undefined.
   */
  lockedProject?: { id: number; jobNumber: string; title: string };
}

export function OrderDrawer({ item, statuses, methods, projects, onClose, onSaved, lockedProject }: DrawerProps) {
  const qc = useQueryClient();
  const { accounts } = useMsal();
  const currentName = accounts[0]?.name ?? accounts[0]?.username ?? 'You';
  const drawerRef = useRef<HTMLDivElement>(null);
  useFocusTrap(drawerRef, { onEscape: onClose });

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // "Other" carrier mode: on when an existing carrier isn't one of the presets.
  const [carrierOther, setCarrierOther] = useState(() => {
    const c = (item?.carrier ?? '').trim();
    return c !== '' && !CARRIER_PRESETS.some(p => p.toLowerCase() === c.toLowerCase());
  });

  /**
   * What the requestor picker opens on, so a save can tell an untouched
   * picker from a deliberate one and send the field only when it MOVED (#543).
   * An external requestor — a name with no Project Tracker user behind it —
   * gets its own option rather than sitting on "none"; see
   * KEEP_EXTERNAL_REQUESTOR for why that has to be a distinct value.
   */
  const initialRequestor = item ? initialRequestorValue(item) : '';
  /** The user id alone, for keeping a removed user selectable below. */
  const initialRequestorId = item?.requestorId ?? '';

  /**
   * Every user, for the requestor picker. Removed people are filtered out
   * below EXCEPT one already on the order — dropping them would render the
   * select blank and a save would then quietly reassign the order.
   */
  const { data: users } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list(),
    staleTime: 5 * 60 * 1000,
  });
  const requestorOptions = useMemo(
    () => (users ?? [])
      .filter(u => !u.deletedAt || u.id === initialRequestorId)
      .map(u => ({ id: u.id, label: u.displayName ?? u.email ?? u.id }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    [users, initialRequestorId],
  );

  const [form, setForm] = useState<Record<string, string>>(() => ({
    itemOrdered:          item?.itemOrdered ?? '',
    requestorId:          initialRequestor,
    projectId:            String(item?.projectId ?? lockedProject?.id ?? ''),
    quantity:             item?.quantity ?? '',
    vendor:               item?.vendor ?? '',
    statusId:             String(item?.status?.id ?? 2),
    deliveryMethodId:     String(item?.deliveryMethod?.id ?? ''),
    orderDate:            toDateInputValue(item?.orderDate),
    expectedDeliveryDate: toDateInputValue(item?.expectedDeliveryDate),
    scheduledArrivalDate: toDateInputValue(item?.scheduledArrivalDate),
    carrier:              item?.carrier ?? '',
    trackingNumber:       item?.trackingNumber ?? '',
    trackingUrl:          item?.trackingUrl ?? '',
    description:          item?.description ?? '',
    notes:                item?.notes ?? '',
  }));

  const f = (k: string) => form[k] ?? '';
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm(p => ({ ...p, [k]: e.target.value }));

  const handleSave = useCallback(async () => {
    setSaving(true); setSaveError(null);
    const ne = (v: string) => v === '' ? null : v;
    const payload: Record<string, unknown> = {
      itemOrdered: ne(f('itemOrdered')),
      projectId: f('projectId') ? Number(f('projectId')) : null,
      quantity: ne(f('quantity')),
      vendor: ne(f('vendor')),
      statusId: Number(f('statusId')) || 2,
      deliveryMethodId: f('deliveryMethodId') ? Number(f('deliveryMethodId')) : null,
      orderDate: ne(f('orderDate')),
      expectedDeliveryDate: ne(f('expectedDeliveryDate')),
      scheduledArrivalDate: ne(f('scheduledArrivalDate')),
      carrier: ne(f('carrier')),
      trackingNumber: ne(f('trackingNumber')),
      trackingUrl: ne(f('trackingUrl')),
      description: ne(f('description')),
      notes: ne(f('notes')),
    };

    // Only when it MOVED — see `requestorPatch` for the three cases and why an
    // untouched picker has to send nothing at all.
    Object.assign(payload, requestorPatch(f('requestorId'), initialRequestor));

    try {
      if (item) await receivingApi.update(item.id, payload);
      else await receivingApi.create(payload);
      qc.invalidateQueries({ queryKey: ['receiving'] });
      onSaved();
    } catch (err: any) {
      setSaveError(err?.response?.data?.message ?? err.message);
    }
    setSaving(false);
  }, [form, item, onSaved, qc, initialRequestor]);

  const deleteMutation = useMutation({
    mutationFn: () => receivingApi.delete(item!.id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['receiving'] }); onClose(); },
  });

  const inp: React.CSSProperties = { border: '1px solid var(--border)', borderRadius: 'var(--ew-r-md)', padding: '6px 10px', fontSize: 13, width: '100%', fontFamily: 'inherit', background: '#fff', color: 'var(--text)' };
  const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px', color: 'var(--gray)', marginBottom: 3, display: 'block' };
  const fw: React.CSSProperties = { display: 'flex', flexDirection: 'column' };
  const g2: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 14px' };
  const sh = (title: string) => (
    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.8px', color: 'var(--ew)', borderBottom: '1px solid var(--border)', paddingBottom: 6, marginBottom: 10, marginTop: 18 }}>
      {title}
    </div>
  );

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 200 }} />
      <div ref={drawerRef} style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 560, background: '#fff', zIndex: 201, overflowY: 'auto', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>{item ? item.title : 'New Order'}</div>
            <div style={{ fontSize: 12, color: 'var(--gray)' }}>
              Requestor: <span style={{ color: 'var(--ew)', fontWeight: 600 }}>{item ? (item.requestorName ?? '—') : currentName}</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {item && (
              <button onClick={() => { if (confirm('Delete this order?')) deleteMutation.mutate(); }}
                style={{ fontSize: 12, color: '#b91c1c', border: '1px solid #fca5a5', borderRadius: 'var(--ew-r-md)', padding: '5px 10px', background: '#fff', cursor: 'pointer' }}>
                Delete
              </button>
            )}
            <button onClick={onClose} style={{ fontSize: 12, color: 'var(--gray)', border: '1px solid var(--border)', borderRadius: 'var(--ew-r-md)', padding: '5px 10px', background: '#fff', cursor: 'pointer' }}>Cancel</button>
            <button onClick={handleSave} disabled={saving} style={{ fontSize: 12, background: 'var(--ew)', color: '#fff', border: 'none', borderRadius: 'var(--ew-r-md)', padding: '6px 14px', cursor: 'pointer', fontWeight: 600, opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button onClick={onClose} aria-label="Close drawer" style={{ fontSize: 18, color: 'var(--gray)', background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px' }}>✕</button>
          </div>
        </div>

        {saveError && <div style={{ margin: '8px 18px 0', padding: '8px 12px', background: '#FBE4DF', color: '#8F2414', borderRadius: 'var(--ew-r-md)', fontSize: 12 }}>{saveError}</div>}

        <div style={{ padding: '4px 18px 32px' }}>
          {sh('Order')}
          <div style={fw}>
            <label style={lbl}>Item Ordered</label>
            <input style={inp} value={f('itemOrdered')} onChange={set('itemOrdered')} placeholder="e.g. Fiberglass downspout gutter" />
          </div>
          <div style={{ ...g2, marginTop: 8 }}>
            <div style={fw}>
              <label style={lbl}>Project{lockedProject ? '' : ' (optional)'}</label>
              {lockedProject ? (
                // Project-workspace use: pre-scoped to this job, not changeable.
                <div style={{ ...inp, background: 'var(--surface)', color: 'var(--text)', display: 'flex', alignItems: 'center' }}>
                  #{lockedProject.jobNumber} — {lockedProject.title}
                </div>
              ) : (
                <select style={inp} value={f('projectId')} onChange={set('projectId')}>
                  <option value="">(none / general)</option>
                  {projects.map(p => <option key={p.id} value={p.id}>#{p.jobNumber} — {p.title}</option>)}
                </select>
              )}
            </div>
            <div style={fw}>
              <label style={lbl}>Quantity</label>
              <input style={inp} value={f('quantity')} onChange={set('quantity')} placeholder="e.g. 1 set" />
            </div>
            <div style={fw}>
              <label style={lbl}>Vendor</label>
              <input style={inp} value={f('vendor')} onChange={set('vendor')} />
            </div>
            <div style={fw}>
              <label style={lbl}>Delivery Method</label>
              <select style={inp} value={f('deliveryMethodId')} onChange={set('deliveryMethodId')}>
                <option value="">—</option>
                {methods.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
              </select>
            </div>
            {item && (
              <div style={fw}>
                <label style={lbl}>Status</label>
                <select style={inp} value={f('statusId')} onChange={set('statusId')}>
                  {statuses.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
              </div>
            )}
            {/* Editable only on an existing order: a new one takes the signed-in
                user, which the header already states. Before #543 there was no
                input at all, so an order whose requestor had been lost could
                not be put right from here. */}
            {item && (
              <div style={fw}>
                <label style={lbl}>Requestor</label>
                <select style={inp} value={f('requestorId')} onChange={set('requestorId')}>
                  <option value="">—</option>
                  {initialRequestor === KEEP_EXTERNAL_REQUESTOR && (
                    <option value={KEEP_EXTERNAL_REQUESTOR}>{item.requestorName} (not a Project Tracker user)</option>
                  )}
                  {requestorOptions.map(u => <option key={u.id} value={u.id}>{u.label}</option>)}
                </select>
              </div>
            )}
          </div>

          {sh('Dates')}
          <div style={g2}>
            <div style={fw}>
              <label style={lbl}>Order Date</label>
              <input type="date" style={inp} value={f('orderDate')} onChange={set('orderDate')} />
            </div>
            <div style={fw}>
              <label style={lbl}>Expected Delivery</label>
              <input type="date" style={inp} value={f('expectedDeliveryDate')} onChange={set('expectedDeliveryDate')} />
            </div>
            <div style={fw}>
              <label style={lbl}>Scheduled On-Site</label>
              <input type="date" style={inp} value={f('scheduledArrivalDate')} onChange={set('scheduledArrivalDate')} />
            </div>
          </div>

          {sh('Tracking')}
          <div style={g2}>
            <div style={fw}>
              <label style={lbl}>Carrier</label>
              {/* Real dropdown (always re-openable); "Other…" reveals a free-text
                  field so freight carriers still work. UPS/FedEx auto-build a
                  tracking deep-link; Amazon needs a pasted URL. */}
              <select style={inp} value={carrierOther ? 'Other' : f('carrier')}
                onChange={e => {
                  const v = e.target.value;
                  if (v === 'Other') { setCarrierOther(true); setForm(p => ({ ...p, carrier: '' })); }
                  else { setCarrierOther(false); setForm(p => ({ ...p, carrier: v })); }
                }}>
                <option value="">—</option>
                {CARRIER_PRESETS.map(c => <option key={c} value={c}>{c}</option>)}
                <option value="Other">Other…</option>
              </select>
              {carrierOther && (
                <input style={{ ...inp, marginTop: 6 }} value={f('carrier')} onChange={set('carrier')} placeholder="Carrier name" />
              )}
            </div>
            <div style={fw}>
              <label style={lbl}>Tracking Number</label>
              <input style={inp} value={f('trackingNumber')} onChange={set('trackingNumber')} />
            </div>
          </div>
          <div style={{ ...fw, marginTop: 8 }}>
            <label style={lbl}>Tracking URL (optional — overrides carrier link)</label>
            <input style={inp} value={f('trackingUrl')} onChange={set('trackingUrl')} placeholder="https://…" />
            {/^amazon/i.test(f('carrier').trim()) && (
              <span style={{ fontSize: 11, color: 'var(--gray)', marginTop: 3 }}>
                Amazon has no plain tracking number — paste the order/tracking URL here so the Track link works.
              </span>
            )}
          </div>

          {sh('Details')}
          <div style={fw}>
            <label style={lbl}>Description</label>
            <textarea style={{ ...inp, minHeight: 54, resize: 'vertical' }} value={f('description')} onChange={set('description')} />
          </div>
          <div style={{ ...fw, marginTop: 8 }}>
            <label style={lbl}>Notes</label>
            <textarea style={{ ...inp, minHeight: 54, resize: 'vertical' }} value={f('notes')} onChange={set('notes')} />
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Tracking cell ───────────────────────────────────────────────────────────────

export function TrackingCell({ order }: { order: ReceivingOrder }) {
  if (order.trackingUrl) {
    return (
      <a href={order.trackingUrl} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
        style={{ color: 'var(--ew)', fontWeight: 600, textDecoration: 'none', fontSize: 12 }}
        title={order.trackingNumber ?? order.trackingUrl}>
        Track ↗
      </a>
    );
  }
  if (order.trackingNumber) {
    return <span style={{ fontSize: 12, color: 'var(--gray)' }}>{order.trackingNumber}</span>;
  }
  return <span style={{ color: 'var(--gray)' }}>—</span>;
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const COL_COUNT = 8;

export default function ReceivingPage() {
  const [search, setSearch] = useState('');
  const [statusId, setStatusId] = useState<number | undefined>(undefined);
  const [projectId, setProjectId] = useState<number | undefined>(undefined);
  const [includeClosed, setIncludeClosed] = useState(false);

  const [showNew, setShowNew] = useState(false);
  const [editTarget, setEditTarget] = useState<ReceivingOrder | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('status');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const handleSort = (key: SortKey) => {
    if (key === sortKey) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };

  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => usersApi.me(), staleTime: 5 * 60 * 1000 });
  const canEdit = (me?.roles.includes('Admin') ?? false) || (me?.permissions.includes('receiving.edit') ?? false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['receiving', { search, statusId, projectId }],
    queryFn: () => receivingApi.list({
      search: search || undefined,
      statusId,
      projectId,
    }) as Promise<ReceivingOrder[]>,
  });

  const { data: statuses } = useQuery({ queryKey: ['receiving-statuses'], queryFn: () => lookupsApi.listReceivingStatuses(), staleTime: 30 * 60 * 1000 });
  const { data: methods } = useQuery({ queryKey: ['receiving-methods'], queryFn: () => lookupsApi.listReceivingDeliveryMethods(), staleTime: 30 * 60 * 1000 });
  const { data: projects } = useQuery({ queryKey: ['receiving-projects'], queryFn: () => receivingApi.projectOptions(), staleTime: 10 * 60 * 1000 });

  const visible = useMemo(() => {
    if (!data) return [];
    if (statusId !== undefined || includeClosed) return data;
    return data.filter(o => o.status.phase !== 'Closed');
  }, [data, statusId, includeClosed]);

  const hiddenClosedCount = useMemo(() => {
    if (!data || statusId !== undefined || includeClosed) return 0;
    return data.filter(o => o.status.phase === 'Closed').length;
  }, [data, statusId, includeClosed]);

  const groups = useMemo(() => groupByProject(visible, sortKey, sortDir), [visible, sortKey, sortDir]);
  const totalCount = visible.length;

  const selectStyle: React.CSSProperties = {
    border: '1px solid var(--border)', borderRadius: 'var(--ew-r-md)', padding: '6px 10px',
    fontSize: 13, background: '#fff', color: 'var(--text)', cursor: 'pointer',
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <h2 style={{ fontWeight: 700, fontSize: 18 }}>Shipping &amp; Receiving</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: 'var(--gray)' }}>{isLoading ? 'Loading…' : `${totalCount} order${totalCount !== 1 ? 's' : ''}`}</span>
          {canEdit && (
            <button onClick={() => setShowNew(true)}
              style={{ fontSize: 12, padding: '5px 14px', background: 'var(--ew)', color: '#fff', border: 'none', borderRadius: 'var(--ew-r-md)', cursor: 'pointer', fontWeight: 600 }}>
              + New Order
            </button>
          )}
        </div>
      </div>

      {/* Filter bar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 16, padding: '12px 16px', background: '#fff', borderRadius: 'var(--ew-r-lg)', border: '1px solid var(--border)' }}>
        <input type="search" placeholder="Search item, vendor, tracking #, job # or project…" value={search} onChange={e => setSearch(e.target.value)}
          style={{ ...selectStyle, minWidth: 260, flexGrow: 1 }} />
        <select value={statusId ?? ''} onChange={e => setStatusId(e.target.value ? Number(e.target.value) : undefined)} style={selectStyle}>
          <option value="">All active</option>
          {(statuses ?? []).map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
        <select value={projectId ?? ''} onChange={e => setProjectId(e.target.value ? Number(e.target.value) : undefined)} style={selectStyle}>
          <option value="">All projects</option>
          {(projects ?? []).map(p => <option key={p.id} value={p.id}>#{p.jobNumber} — {p.title}</option>)}
        </select>
        {statusId === undefined && (
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--gray)', cursor: 'pointer', userSelect: 'none', padding: '0 6px' }}>
            <input type="checkbox" checked={includeClosed} onChange={e => setIncludeClosed(e.target.checked)} />
            Include closed
            {!includeClosed && hiddenClosedCount > 0 && (
              <span style={{ background: 'var(--surface)', color: 'var(--gray)', border: '1px solid var(--border)', borderRadius: 'var(--ew-r-lg)', padding: '1px 8px', fontSize: 11, fontWeight: 600 }}>
                {hiddenClosedCount} hidden
              </span>
            )}
          </label>
        )}
      </div>

      {error && <div style={{ padding: 12, background: '#FBE4DF', color: '#8F2414', borderRadius: 'var(--ew-r-md)', marginBottom: 16, fontSize: 13 }}>{(error as Error).message}</div>}

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 'var(--ew-r-lg)', border: '1px solid var(--border)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '24%' }} />
            <col style={{ width: '16%' }} />
            <col style={{ width: '13%' }} />
            <col style={{ width: '7%' }} />
            <col style={{ width: '11%' }} />
            <col style={{ width: '13%' }} />
            <col style={{ width: '8%' }} />
            <col style={{ width: '8%' }} />
          </colgroup>
          <thead>
            <tr>
              <Th label="Item"      sortKey="item"      current={sortKey} dir={sortDir} onSort={handleSort} />
              <Th label="Status"    sortKey="status"    current={sortKey} dir={sortDir} onSort={handleSort} />
              <Th label="Vendor"    sortKey="vendor"    current={sortKey} dir={sortDir} onSort={handleSort} />
              <Th label="Qty"       sortKey="qty"       current={sortKey} dir={sortDir} onSort={handleSort} />
              <Th label="Expected"  sortKey="expected"  current={sortKey} dir={sortDir} onSort={handleSort} />
              <Th label="Requestor" sortKey="requestor" current={sortKey} dir={sortDir} onSort={handleSort} />
              {['Tracking', ''].map((h, i) => (
                <th key={i} style={{ padding: '8px 10px', textAlign: i === 1 ? 'right' : 'left', borderBottom: '2px solid var(--border)', background: 'var(--surface)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.4px', color: 'var(--gray)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={COL_COUNT} style={{ padding: 32, textAlign: 'center', color: 'var(--gray)' }}>Loading…</td></tr>}
            {!isLoading && groups.length === 0 && <tr><td colSpan={COL_COUNT} style={{ padding: 32, textAlign: 'center', color: 'var(--gray)' }}>No orders match your filters.</td></tr>}
            {groups.map(group => (
              <>
                <tr key={`group-${group.key}`}>
                  <td colSpan={COL_COUNT} style={{ padding: '7px 12px', background: '#EDEDEC', color: 'var(--ew)', fontWeight: 700, fontSize: 12, letterSpacing: '.3px', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)', borderLeft: '3px solid var(--ew)' }}>
                    {group.label}
                    <span style={{ fontWeight: 400, color: 'var(--gray)', marginLeft: 8 }}>({group.items.length})</span>
                  </td>
                </tr>
                {group.items.map((o, i) => {
                  const expanded = expandedId === o.id;
                  const rowBg = expanded ? 'var(--ew-dim)' : (i % 2 === 0 ? '#fff' : 'var(--surface)');
                  return (
                  <>
                    <tr key={o.id}
                      onClick={() => setExpandedId(id => id === o.id ? null : o.id)}
                      style={{ background: rowBg, borderBottom: expanded ? 'none' : '1px solid var(--border)', cursor: 'pointer' }}
                      onMouseEnter={e => { if (!expanded) e.currentTarget.style.background = 'var(--ew-dim)'; }}
                      onMouseLeave={e => { if (!expanded) e.currentTarget.style.background = rowBg; }}>
                      <td style={{ padding: '8px 10px 8px 20px', overflow: 'hidden' }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.itemOrdered ?? o.title}</div>
                      </td>
                      <td style={{ padding: '8px 10px' }}><ReceivingStatusBadge label={o.status.label} /></td>
                      <td style={{ padding: '8px 10px', color: 'var(--gray)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.vendor ?? '—'}</td>
                      <td style={{ padding: '8px 10px', color: 'var(--gray)', fontSize: 12 }}>{o.quantity ?? '—'}</td>
                      <td style={{ padding: '8px 10px', color: 'var(--gray)', fontSize: 12, whiteSpace: 'nowrap' }}>{fmtDate(o.expectedDeliveryDate)}</td>
                      <td style={{ padding: '8px 10px', color: 'var(--gray)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{requestorName(o) || '—'}</td>
                      <td style={{ padding: '8px 10px' }}><TrackingCell order={o} /></td>
                      <td style={{ padding: '8px 8px', textAlign: 'right' }}>
                        {canEdit && (
                          <button onClick={e => { e.stopPropagation(); setEditTarget(o); }}
                            style={{ fontSize: 11, padding: '3px 8px', border: '1px solid var(--border)', borderRadius: 'var(--ew-r-md)', background: '#fff', color: 'var(--text)', cursor: 'pointer' }}>
                            Edit
                          </button>
                        )}
                      </td>
                    </tr>
                    {expanded && (
                      <tr key={`detail-${o.id}`} style={{ borderBottom: '2px solid var(--ew)' }}>
                        <td colSpan={COL_COUNT} style={{ padding: 0 }}>
                          <OrderDetail o={o} canEdit={canEdit}
                            onEdit={() => { setEditTarget(o); setExpandedId(null); }}
                            onClose={() => setExpandedId(null)} />
                        </td>
                      </tr>
                    )}
                  </>
                  );
                })}
              </>
            ))}
          </tbody>
        </table>
      </div>

      {showNew && (
        <OrderDrawer item={null} statuses={statuses ?? []} methods={methods ?? []} projects={projects ?? []}
          onClose={() => setShowNew(false)} onSaved={() => setShowNew(false)} />
      )}
      {editTarget && (
        <OrderDrawer item={editTarget} statuses={statuses ?? []} methods={methods ?? []} projects={projects ?? []}
          onClose={() => setEditTarget(null)} onSaved={() => setEditTarget(null)} />
      )}
    </div>
  );
}
